import React from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { INTAKE_FIELDS, SUCCESS_MEASURES, isIntakeComplete } from "@/data/bridgeData";
import { useEntries } from "@/context/EntriesContext";
import MicButton from "@/components/MicButton";
import { ArrowRight } from "lucide-react";

export default function Intake() {
  const { getEntry, saveEntry } = useEntries();
  const navigate = useNavigate();
  const scales = SUCCESS_MEASURES.filter((m) => m.type === "scale");
  const done = isIntakeComplete(getEntry);

  return (
    <Layout coachContext>
      <div className="label-eyebrow mb-3">Week 0 · Onboarding</div>
      <h1 className="font-serif text-4xl sm:text-5xl font-light text-stone-900 mb-3">Intake & baseline</h1>
      <p className="max-w-2xl text-stone-600 leading-relaxed mb-10">
        Where you're starting from, what you're moving toward, and an honest baseline. This frames the whole journey –
        we'll revisit these same scores at Week 8 and Week 16.
      </p>

      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 rounded-sm border border-stone-200 bg-white p-8 space-y-7">
          <div className="label-eyebrow">Intake</div>
          {INTAKE_FIELDS.map((f) => (
            <div key={f.id}>
              <label className="font-serif text-xl text-stone-900">{f.label}</label>
              <textarea
                data-testid={`intake-${f.id}`}
                value={getEntry(`intake:${f.id}`, "")}
                onChange={(e) => saveEntry(`intake:${f.id}`, e.target.value)}
                rows={3}
                placeholder="Write or speak freely…"
                className="mt-2 w-full resize-y bg-transparent border-b border-stone-300 py-2 text-stone-800 focus:outline-none focus:border-brand-primary transition-colors"
              />
              <div className="mt-1.5 flex justify-end">
                <MicButton getValue={() => getEntry(`intake:${f.id}`, "")} onChange={(v) => saveEntry(`intake:${f.id}`, v)} testid={`intake-${f.id}-mic`} />
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-sm border border-stone-200 bg-brand-parchment/40 p-7 h-fit">
          <div className="label-eyebrow mb-5">Baseline confidence (1–10)</div>
          <div className="space-y-5">
            {scales.map((m) => {
              const key = `measure:week0:${m.id}`;
              const val = Number(getEntry(key, 0)) || 0;
              return (
                <div key={m.id}>
                  <div className="flex justify-between items-baseline mb-1.5">
                    <span className="text-sm text-stone-600 leading-tight pr-2">{m.label}</span>
                    <span className="font-serif text-xl text-brand-primary">{val}</span>
                  </div>
                  <input
                    data-testid={`baseline-${m.id}`}
                    type="range" min="0" max="10" value={val}
                    onChange={(e) => saveEntry(key, Number(e.target.value), true)}
                    className="bridge-range"
                  />
                </div>
              );
            })}
          </div>
          <button
            data-testid="intake-continue"
            disabled={!done}
            onClick={() => done && navigate("/sessions/1")}
            className="group mt-7 inline-flex w-full items-center justify-center gap-2 rounded-sm bg-brand-primary px-5 py-3 text-white transition-all duration-300 hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {done ? "Begin Session 1" : "Answer all intake questions"} <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
          </button>
          {!done && (
            <p data-testid="intake-incomplete-hint" className="mt-3 text-center text-xs text-stone-500">
              Session 1 unlocks once every intake question is answered.
            </p>
          )}
        </div>
      </div>
    </Layout>
  );
}
